import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, XCircle, AlertTriangle, Bot, Shield, Zap, Target } from "lucide-react";
import { Button } from "@/components/ui/button";

const NoQ5VsRelevanceAI = () => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "NoQ5 vs Relevance AI — CRM Agent Comparison 2026",
      "description": "Detailed comparison of NoQ5 and Relevance AI for CRM automation, enrichment, and sales operations. See which CRM AI agent is better for your revenue team.",
      "url": "https://noq5.ai/noq5-vs-relevance-ai"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://noq5.ai" },
        { "@type": "ListItem", "position": 2, "name": "Comparisons", "item": "https://noq5.ai/comparison" },
        { "@type": "ListItem", "position": 3, "name": "NoQ5 vs Relevance AI", "item": "https://noq5.ai/noq5-vs-relevance-ai" }
      ]
    }
  ];

  const comparison = [
    { feature: "Core Approach", noq5: "Purpose-built CRM AI agent — executes autonomously", relevance: "General AI agent builder — requires configuration", winner: "noq5" },
    { feature: "CRM Updates", noq5: "40+ fields auto-updated from conversations", relevance: "Enrichment-focused, limited activity logging", winner: "noq5" },
    { feature: "Meeting Intelligence", noq5: "Full call/meeting transcription + context extraction", relevance: "No native meeting intelligence", winner: "noq5" },
    { feature: "Activity Capture", noq5: "Email, calls, Slack, LinkedIn, Zoom — all channels", relevance: "Limited to configured integrations", winner: "noq5" },
    { feature: "CRM Enrichment", noq5: "30+ fields from 15+ sources, continuous refresh", relevance: "Strong enrichment via multi-source aggregation", winner: "tie" },
    { feature: "Pipeline Forecasting", noq5: "Evidence-based AI forecasting from actual engagement", relevance: "No native forecasting", winner: "noq5" },
    { feature: "Deal Risk Detection", noq5: "Proactive alerts within 48h of declining engagement", relevance: "No native risk detection", winner: "noq5" },
    { feature: "Setup Time", noq5: "15 minutes — connect CRM + channels via OAuth", relevance: "Hours to days — build and configure agents", winner: "noq5" },
    { feature: "CRM Support", noq5: "Salesforce + HubSpot + Pipedrive natively", relevance: "Salesforce + HubSpot via integrations", winner: "noq5" },
    { feature: "Flexibility", noq5: "Opinionated — built for revenue teams", relevance: "Flexible — build any AI agent/workflow", winner: "relevance" },
    { feature: "Marketplace", noq5: "Focused product — no marketplace needed", relevance: "Large template marketplace", winner: "relevance" },
    { feature: "Security", noq5: "SOC 2 Type II, GDPR, EU data residency", relevance: "SOC 2, GDPR compliant", winner: "tie" },
    { feature: "Pricing Model", noq5: "Per-seat, all features included", relevance: "Usage-based, credits for agent runs", winner: "noq5" },
  ];

  return (
    <Layout>
      <SEOHead
        title="NoQ5 vs Relevance AI — CRM Agent Comparison 2026 | NoQ5"
        description="NoQ5 vs Relevance AI: which CRM AI agent is better? Compare features, CRM updates, enrichment, forecasting, meeting intelligence, and pricing for revenue teams."
        keywords="NoQ5 vs Relevance AI, Relevance AI alternative, Relevance AI competitor, CRM agent comparison, best CRM AI agent, Relevance AI CRM, AI agent builder vs CRM agent, CRM enrichment comparison, sales AI agent comparison"
        canonicalUrl="https://noq5.ai/noq5-vs-relevance-ai"
        jsonLd={jsonLd}
      />

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="text-sm text-muted-foreground mb-6">
              <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/comparison" className="hover:text-primary">Comparisons</Link> / <span className="text-foreground">NoQ5 vs Relevance AI</span>
            </nav>
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
              <span className="text-primary">NoQ5</span> vs Relevance AI
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Relevance AI is a flexible AI agent builder. NoQ5 is a purpose-built CRM AI agent that <em>does the work</em>. Here's exactly how they compare for revenue teams.
            </p>
          </div>
        </div>
      </section>

      {/* TL;DR */}
      <section className="py-12 bg-muted/30 border-y border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-foreground mb-6 text-center">The Bottom Line</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-3">Choose Relevance AI if:</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" /> You want to build custom AI agents for diverse use cases</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" /> You have a technical team to configure and maintain agents</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" /> Your needs span beyond CRM (marketing, support, ops)</li>
              </ul>
            </div>
            <div className="bg-primary/5 border border-primary/30 rounded-xl p-6">
              <h3 className="font-semibold text-foreground mb-3">Choose NoQ5 if:</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /> You want your CRM to run itself — zero manual work</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /> You need meeting intelligence + CRM updates + forecasting in one agent</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /> You want to deploy in 15 minutes, not days</li>
                <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" /> Your team is revenue-focused (sales, RevOps, CROs)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Feature-by-Feature Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden border border-border text-sm">
              <thead>
                <tr className="bg-muted">
                  <th className="text-left p-3 font-semibold text-foreground">Feature</th>
                  <th className="p-3 font-semibold text-primary text-center">NoQ5</th>
                  <th className="p-3 font-semibold text-foreground text-center">Relevance AI</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row) => (
                  <tr key={row.feature} className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">{row.feature}</td>
                    <td className={`p-3 text-center text-sm ${row.winner === 'noq5' ? 'bg-primary/5 font-semibold text-foreground' : 'text-muted-foreground'}`}>{row.noq5}</td>
                    <td className={`p-3 text-center text-sm ${row.winner === 'relevance' ? 'bg-muted font-semibold text-foreground' : 'text-muted-foreground'}`}>{row.relevance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Deep Dive */}
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground mb-8">The Core Difference: Builder vs. Doer</h2>
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
            <p>
              <strong>Relevance AI</strong> is an AI agent builder platform. It's a powerful tool for creating custom AI workflows across multiple business functions — GTM, support, operations, marketing. You can build agents that enrich CRM data, draft emails, classify leads, and automate research. The flexibility is real.
            </p>
            <p>
              <strong>NoQ5</strong> is the opposite architectural choice. Instead of giving you a toolkit to build agents, NoQ5 <em>is</em> the agent. It's pre-built, pre-trained, and purpose-designed for one job: making your CRM run itself. It captures every sales interaction, updates every field, enriches every contact, forecasts your pipeline, and alerts you to deal risks — out of the box, in 15 minutes.
            </p>
            <p>
              The trade-off is clear:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Relevance AI</strong> gives you more flexibility but requires more setup, maintenance, and technical skill</li>
              <li><strong>NoQ5</strong> gives you less flexibility but delivers immediate value with zero configuration for revenue teams</li>
            </ul>

            <h3 className="text-2xl font-bold text-foreground">Where NoQ5 Wins Decisively</h3>
            
            <h4 className="text-xl font-semibold text-foreground">Meeting Intelligence</h4>
            <p>Relevance AI has no native meeting intelligence. NoQ5 records, transcribes, and analyzes every sales call — extracting buying signals, objections, competitor mentions, and action items — then pushes all of this to your CRM automatically. This is the single biggest source of CRM data that Relevance can't touch without significant custom engineering.</p>

            <h4 className="text-xl font-semibold text-foreground">Pipeline Forecasting</h4>
            <p>NoQ5 generates evidence-based pipeline forecasts from actual engagement data — not rep-submitted probabilities. Relevance AI has no forecasting capability. For CROs and revenue leaders, this alone can justify the switch.</p>

            <h4 className="text-xl font-semibold text-foreground">Zero Setup Time</h4>
            <p>NoQ5 deploys in 15 minutes. Connect your CRM, connect your communication tools, and the agent starts working. Relevance AI requires building agent workflows, configuring triggers, testing outputs, and maintaining the system over time. For teams without dedicated technical resources, this is the deciding factor.</p>

            <h3 className="text-2xl font-bold text-foreground">Where Relevance AI Has an Edge</h3>
            <p>
              Relevance AI is genuinely better if you need to build custom AI agents beyond CRM — for marketing automation, customer support, HR workflows, or cross-functional processes. Their marketplace of templates and building blocks makes it fast to prototype new agent workflows. If your needs extend far beyond revenue operations, Relevance's flexibility matters.
            </p>
            <p>
              But if your primary goal is <strong>making your CRM run itself</strong> — with meeting intelligence, pipeline forecasting, deal risk detection, and autonomous updates — NoQ5 is purpose-built for exactly that job.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Your CRM Should Run Itself
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Not suggestions. Not a builder. Not a marketplace. Execution. Deploy NoQ5 in 15 minutes and never touch a CRM field again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/trial">
              <Button size="lg" className="text-lg px-8 py-6 bg-primary hover:bg-primary/90 text-primary-foreground">
                Try NoQ5 Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/crm-agent">
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-border text-foreground hover:bg-accent">
                How Our CRM Agent Works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12 border-t border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-xl font-semibold text-foreground mb-6">More Comparisons</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/noq5-vs-agentforce" className="text-primary hover:underline text-sm">NoQ5 vs Salesforce Agentforce</Link>
            <Link to="/noq5-vs-hubspot-ai" className="text-primary hover:underline text-sm">NoQ5 vs HubSpot AI</Link>
            <Link to="/comparison" className="text-primary hover:underline text-sm">All Comparisons</Link>
            <Link to="/crm-agent" className="text-primary hover:underline text-sm">CRM AI Agent</Link>
            <Link to="/crm-enrichment-agent" className="text-primary hover:underline text-sm">CRM Enrichment Agent</Link>
            <Link to="/why-crm-data-is-broken" className="text-primary hover:underline text-sm">Why CRM Data Is Broken</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NoQ5VsRelevanceAI;
